import { addLine, predictLine, predictTotals, watchPrices } from '@theme/cart-predict';
import { formatMoney } from '@theme/money';
import { fetchJSON, routeUrl } from '@theme/utils';
import { updateCartCount } from '@theme/product-form';

/** Section id of sections/cart-drawer.liquid (rendered with `{% section 'cart-drawer' %}`). */
export const SECTION = 'cart-drawer';

/** Duration of the `gs-liout` animation of a removed line (assets/cart-drawer.css). */
export const LEAVE_MS = 420;

/** Rapid − / + clicks on a line are sent as one request once they pause this long. */
export const BATCH_MS = 250;

const FOCUSABLE = ['a[href]', 'button:not([disabled])', 'input:not([disabled])', '[tabindex]']
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(',');

/**
 * Updates the drawer in place from a fresh render: `[data-cart-region]`s take the new content, the progress
 * bar its `--p` (the width transitions), lines are morphed by `data-key` (their stack animations play).
 *
 * @param {Element} current - The live `<cart-drawer>`.
 * @param {Element} next - The same element, parsed from the Section Rendering API response.
 */
export function renderCart(current, next) {
  for (const region of current.querySelectorAll('[data-cart-region]')) {
    const name = region.getAttribute('data-cart-region');
    const fresh = next.querySelector(`[data-cart-region="${name}"]`);
    if (!fresh) continue;
    const list = region.querySelector('[data-cart-lines]');
    const freshList = fresh.querySelector('[data-cart-lines]');
    if (list && freshList) morphLines(list, freshList);
    else region.innerHTML = fresh.innerHTML;
  }

  const bar = current.querySelector('[data-cart-progress]');
  const freshBar = next.querySelector('[data-cart-progress]');
  if (bar && freshBar) bar.setAttribute('style', freshBar.getAttribute('style') ?? '');

  for (const name of ['data-count', 'data-total']) {
    const value = next.getAttribute(name);
    if (value !== null) current.setAttribute(name, value);
  }
}

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));

/**
 * @param {Element} list
 * @param {Element} freshList
 */
function morphLines(list, freshList) {
  const keyOf = (/** @type {Element} */ el) => el.getAttribute('data-key');
  const freshLines = [...freshList.children];
  const keys = new Set(freshLines.map(keyOf));
  for (const line of [...list.children]) if (!keys.has(keyOf(line))) line.remove();

  /** @type {Element | null} */
  let previous = null;
  for (const freshLine of freshLines) {
    let line = [...list.children].find((el) => keyOf(el) === keyOf(freshLine));
    if (line) {
      const tiles = line.querySelectorAll('[data-st]');
      freshLine.querySelectorAll('[data-st]').forEach((tile, i) => {
        if (tiles[i]) tiles[i].className = tile.className;
      });
      const body = line.querySelector('[data-line-body]');
      const freshBody = freshLine.querySelector('[data-line-body]');
      if (body && freshBody) body.innerHTML = freshBody.innerHTML;
      for (const name of ['style', 'data-qty', 'data-price', 'data-orig']) {
        const value = freshLine.getAttribute(name);
        if (value === null) line.removeAttribute(name);
        else line.setAttribute(name, value);
      }
      // Still in the cart (failed removal rolled back).
      line.classList.remove('out');
    } else {
      line = freshLine;
    }
    /** @type {Element | null} */
    const expected = previous ? previous.nextElementSibling : list.firstElementChild;
    if (line !== expected) list.insertBefore(line, expected);
    previous = line;
  }
}

/**
 * Cart drawer: `<cart-drawer class="gs-cartwrap">` around an overlay and a `[role="dialog"]` panel.
 *
 * - opens on `cart:open` and on clicks on any `[data-cart-open]` (the header cart link, which leads to the
 *   cart page without JS), closes on `[data-cart-close]`, Escape and the overlay; traps and restores focus;
 * - re-renders on `cart:updated` (e.g. after an add to cart) through the Section Rendering API;
 * - − / + / remove buttons (`[data-cart-qty]`, `name="updates[<key>]"`, `value` = new quantity) update the
 *   drawer and the header count at once (optimistic UI, a removed line slides out), then go through
 *   `/cart/change.js` with bundled section rendering. Rapid clicks are batched, requests are sent one at a
 *   time and only the answer to the latest one is rendered; on error the last server state comes back.
 */
export class CartDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  #returnFocus = null;
  /** True while this drawer itself announces a new count (its own `cart:updated` must not re-fetch). */
  #silent = false;
  /** Quantities not sent yet, by line key. @type {Map<string, number>} */
  #pending = new Map();
  #sending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** End of the slide-out of a removed line: the server render waits for it. */
  #leaveUntil = 0;
  /** Last count/total sent to `updateCartCount`. */
  #announced = '';
  /** Last drawer HTML confirmed by the server, restored when a change fails. @type {string | undefined} */
  #confirmed;

  connectedCallback() {
    watchPrices(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('cart:open', this.onOpen);
    document.addEventListener('cart:adding', this.onAdding);
    document.addEventListener('cart:updated', this.onUpdated);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('cart:open', this.onOpen);
    document.removeEventListener('cart:adding', this.onAdding);
    document.removeEventListener('cart:updated', this.onUpdated);
    document.documentElement.classList.remove('cd-lock');
  }

  get panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[role="dialog"]'));
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (this.isOpen) return;
    const active = document.activeElement;
    this.#returnFocus = active instanceof HTMLElement && !this.contains(active) ? active : null;
    this.classList.add('is-open');
    document.documentElement.classList.add('cd-lock');
    const close = /** @type {HTMLElement | null} */ (this.querySelector('.cd-head [data-cart-close]'));
    (close ?? this.panel)?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    document.documentElement.classList.remove('cd-lock');
    this.#returnFocus?.focus({ preventScroll: true });
    this.#returnFocus = null;
  }

  onOpen = () => this.open();

  /** Add to cart started: show the line and open now; roll back if it fails. @param {DocumentEventMap['cart:adding']} event */
  onAdding = async (event) => {
    const { item, request } = event.detail;
    this.#confirmed ??= this.outerHTML;
    const wasOpen = this.isOpen;
    this.showError(null);
    addLine(this, item, this.format);
    this.#announce(Number(this.getAttribute('data-count')));
    this.open();
    try {
      await request;
    } catch {
      this.render(this.#confirmed);
      this.#announce(Number(this.getAttribute('data-count')));
      if (!wasOpen) this.close();
    }
  };

  /** @param {DocumentEventMap['cart:updated']} event */
  onUpdated = (event) => {
    if (this.#silent) return;
    // An add to cart may bundle the drawer HTML (product-form.js): no second request.
    const html = event.detail?.sections?.[SECTION];
    if (html) {
      this.#confirmed = html;
      this.render(html);
    } else this.refresh();
  };

  /** @returns {import('@theme/cart-predict').Format | null} */
  get format() {
    const format = this.dataset.moneyFormat;
    return format ? (cents) => formatMoney(cents, format) : null;
  }

  /** @param {MouseEvent} event */
  onDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-cart-open]') : null;
    if (!trigger || this.contains(trigger)) return;
    // On the cart page itself, the link just reloads the page (its lines would not follow the drawer).
    if (trigger instanceof HTMLAnchorElement && trigger.pathname === window.location.pathname) return;
    event.preventDefault();
    this.open();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-cart-close]')) {
      this.close();
      return;
    }
    const button = /** @type {HTMLButtonElement | null | undefined} */ (target?.closest('button[data-cart-qty]'));
    if (!button) return;
    event.preventDefault();
    const line = button.closest('[data-key]');
    const key = line?.getAttribute('data-key');
    if (line && key && !line.classList.contains('out')) this.change(key, Math.max(0, Number(button.value)), line);
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const items = /** @type {HTMLElement[]} */ ([...this.panel.querySelectorAll(FOCUSABLE)]).filter(
      (el) => !el.closest('[hidden]'),
    );
    const first = items[0];
    const last = items.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Sets a line's quantity (0 removes it, with the slide-out animation): the drawer updates at once, the
   * request follows (batched with the next clicks on the same line).
   *
   * @param {string} key - Line item key.
   * @param {number} quantity
   * @param {Element} [line] - The line element, updated optimistically.
   */
  change(key, quantity, line) {
    this.#confirmed ??= this.outerHTML;
    this.showError(null);
    if (line) {
      const format = this.format;
      const before = Number(line.getAttribute('data-qty')) || 0;
      const count =
        Number(this.getAttribute('data-count') ?? this.querySelector('[data-cart-region="count"]')?.textContent) || 0;
      const delta = predictLine(line, quantity, format);
      predictTotals(this, Math.max(0, count + quantity - before), delta, format);
      this.#announce(Math.max(0, count + quantity - before));
      if (quantity === 0) {
        if (line.contains(document.activeElement)) this.panel?.focus({ preventScroll: true });
        line.classList.add('out');
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.#leaveUntil = Date.now() + (reduce ? 0 : LEAVE_MS);
      }
    }
    this.#pending.set(key, quantity);
    this.setAttribute('aria-busy', 'true');
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#flush(), BATCH_MS);
  }

  /** Sends the pending changes, one request at a time. */
  async #flush() {
    if (this.#sending) return; // picked up again when the current request ends
    const [entry] = this.#pending;
    if (!entry) {
      this.removeAttribute('aria-busy');
      return;
    }
    const [key, quantity] = entry;
    this.#pending.delete(key);
    this.#sending = true;
    try {
      /** @type {{ item_count: number, sections?: Record<string, string | null> }} */
      const cart = await fetchJSON(routeUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION, sections_url: window.location.pathname }),
      });
      const html = cart.sections?.[SECTION];
      if (html) this.#confirmed = html;
      // An older answer would undo newer clicks: only the state after the last change is rendered.
      if (!this.#pending.size) await wait(this.#leaveUntil - Date.now());
      if (!this.#pending.size) {
        this.render(html);
        this.#announce(cart.item_count);
      }
    } catch (e) {
      clearTimeout(this.#timer);
      this.#pending.clear();
      this.render(this.#confirmed);
      this.#announce(Number(this.getAttribute('data-count') ?? NaN));
      this.showError(e instanceof Error && e.message ? e.message : '');
      // The confirmed state may predate earlier successful changes: ask the server.
      this.refresh();
    } finally {
      this.#sending = false;
      if (this.#pending.size) this.#flush();
      else this.removeAttribute('aria-busy');
    }
  }

  /** Re-renders the drawer from the server (after a change made elsewhere, e.g. an add to cart). */
  async refresh() {
    try {
      // Not `/cart?sections=`: with JSON accepted it answers with the cart, not sections.
      /** @type {Record<string, string | null>} */
      const sections = await fetchJSON(`${routeUrl('')}?sections=${SECTION}`);
      if (this.#pending.size || this.#sending) return; // newer local changes win
      this.#confirmed = sections[SECTION] ?? this.#confirmed;
      this.render(sections[SECTION]);
    } catch {
      // Keep the current content: the next update or page load will catch up.
    }
  }

  /**
   * Applies a Section Rendering API payload, keeping focus on the equivalent control when it was replaced.
   *
   * @param {string | null | undefined} html
   */
  render(html) {
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (!next) return;

    const active = document.activeElement;
    const hadFocus = active instanceof Element && this.contains(active);
    const focusKey = hadFocus ? active.closest('[data-key]')?.getAttribute('data-key') : null;
    const focusAction = hadFocus ? active.getAttribute('data-action') : null;

    renderCart(this, next);

    if (hadFocus && !this.contains(document.activeElement)) {
      const line = focusKey
        ? [...this.querySelectorAll('[data-key]')].find((el) => el.getAttribute('data-key') === focusKey)
        : null;
      const target = /** @type {HTMLElement | null | undefined} */ (
        line?.querySelector(`[data-action="${focusAction}"]`) ?? this.panel
      );
      target?.focus({ preventScroll: true });
    }
  }

  /**
   * Shows an error in `[data-cart-error]` (role="alert").
   *
   * @param {string | null} message - `null` hides it; `''` shows the generic message of its `data-error`.
   */
  showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-cart-error]'));
    if (!error) return;
    error.textContent = message === null ? '' : message || error.dataset.error || '';
    error.hidden = !error.textContent;
  }

  /** @param {number} itemCount */
  #announce(itemCount) {
    if (!Number.isFinite(itemCount)) return;
    const total = this.getAttribute('data-total');
    // The server confirming the optimistic state must not make the header badge bounce a second time.
    const state = `${itemCount}/${total}`;
    if (state === this.#announced) return;
    this.#announced = state;
    this.#silent = true;
    try {
      updateCartCount(itemCount, total === null ? {} : { total: Number(total) });
    } finally {
      this.#silent = false;
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
